import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";

/**
 * useAutosave — the reusable on-change persistence primitive.
 *
 * Generalizes the two proven jQuery autosave implementations
 * (assets/js/pages/action-plan-actions-selection.js and
 * communication-customization.js) into one hook so on-change saving is the
 * default for every form, not the exception. It provides:
 *
 *  - a debounced save (default 400ms, matching the legacy timer);
 *  - abort-on-collision / last-write-wins: a new save aborts the previous
 *    in-flight request so out-of-order responses can't clobber newer state;
 *  - a teardown flush: on pagehide / tab-hidden it sends the latest snapshot
 *    via navigator.sendBeacon (the only delivery the browser guarantees during
 *    unload), so a change followed by a quick close within the debounce window
 *    isn't silently lost;
 *  - a visible status (idle / saving / saved / error) so saves are never
 *    silent (today's silent-save bug);
 *  - allow-empty / partial-save support via `shouldSave`.
 *
 * The async save runs through a TanStack Query mutation; the debounce, abort
 * and beacon orchestration wrap it.
 */

export type SaveStatus = "idle" | "saving" | "saved" | "error";

export interface BeaconPayload {
  url: string;
  /** Serialized request body (e.g. a JSON string) wrapped in a typed Blob for
   * navigator.sendBeacon so the content-type is preserved. */
  body: BlobPart;
  contentType?: string;
}

export interface UseAutosaveOptions<TData> {
  /**
   * Persist `data`. Must reject on failure. The AbortSignal fires when a newer
   * save supersedes this one (last-write-wins) — honour it in fetch/openapi-fetch
   * calls so the stale request is cancelled.
   */
  save: (data: TData, signal: AbortSignal) => Promise<unknown>;
  /** Debounce window in ms before a change is sent. Default 400. */
  debounceMs?: number;
  /**
   * Skip persisting snapshots not worth saving (e.g. an untouched/empty form).
   * Defaults to always-save. (The communication flow's `allow_empty` analogue.)
   */
  shouldSave?: (data: TData) => boolean;
  /**
   * Build the unload-flush payload for navigator.sendBeacon. Return null to
   * skip the beacon for this snapshot. When omitted, no beacon flush happens
   * (the in-app debounced save still runs) — provide it for forms where losing
   * the last keystrokes on a tab close matters.
   */
  beacon?: (data: TData) => BeaconPayload | null;
  onSaved?: (data: TData) => void;
  onError?: (error: unknown, data: TData) => void;
}

export interface UseAutosaveResult<TData> {
  /** Visible save status for the UI. */
  status: SaveStatus;
  /** Most recent save error, if status === "error". */
  error: unknown;
  /** Epoch ms of the last successful save, or null. */
  lastSavedAt: number | null;
  /** True while a change is pending (debounced) or a request is in flight. */
  isDirty: boolean;
  /** Queue a debounced save of the latest snapshot. Call on every change. */
  save: (data: TData) => void;
  /** Cancel a pending debounced save without sending it. */
  cancel: () => void;
  /** Send the latest pending snapshot immediately (returns when settled). */
  flush: () => Promise<void>;
}

export function useAutosave<TData>(
  options: UseAutosaveOptions<TData>,
): UseAutosaveResult<TData> {
  const {
    save: saveFn,
    debounceMs = 400,
    shouldSave,
    beacon,
    onSaved,
    onError,
  } = options;

  const [status, setStatus] = useState<SaveStatus>("idle");
  const [error, setError] = useState<unknown>(null);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  // Latest snapshot + the last snapshot we actually saved, kept in refs so the
  // unload listener and the debounce callback always see current values
  // without re-subscribing.
  const latestData = useRef<TData | null>(null);
  const hasPending = useRef(false); // debounced save scheduled, not yet sent
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlight = useRef<AbortController | null>(null);

  // Keep option callbacks fresh without retriggering effects.
  const refs = useRef({ saveFn, shouldSave, beacon, onSaved, onError });
  refs.current = { saveFn, shouldSave, beacon, onSaved, onError };

  // `flush` needs the current status but must not change identity when it
  // moves. Callers mount it as an unmount-flush — `useEffect(() => () =>
  // flush(), [flush])` — so a `flush` that changed on every status transition
  // re-ran that effect, and its cleanup called flush again. With status
  // "error" (where flush deliberately retries) that closes a loop: save →
  // error → new flush identity → cleanup flushes → save → … a request storm
  // for as long as the failure persists.
  const statusRef = useRef(status);
  statusRef.current = status;

  const mutation = useMutation<unknown, unknown, { data: TData; signal: AbortSignal }>({
    mutationFn: ({ data, signal }) => refs.current.saveFn(data, signal),
  });
  const mutateAsync = mutation.mutateAsync;

  const runSave = useCallback(
    async (data: TData) => {
      // Abort the previous in-flight request: the newer write wins, and the
      // stale response can't overwrite it.
      if (inFlight.current) inFlight.current.abort();
      const controller = new AbortController();
      inFlight.current = controller;

      setStatus("saving");
      setIsDirty(true);
      try {
        await mutateAsync({ data, signal: controller.signal });
        // Only settle to "saved" if this is still the current request (a newer
        // save may have superseded us while awaiting).
        if (inFlight.current === controller) {
          inFlight.current = null;
          setStatus("saved");
          setError(null);
          setLastSavedAt(Date.now());
          setIsDirty(hasPending.current);
          refs.current.onSaved?.(data);
        }
      } catch (err) {
        if (controller.signal.aborted) {
          // Intentional supersede/teardown abort — not a failure to surface.
          return;
        }
        if (inFlight.current === controller) inFlight.current = null;
        // The change never persisted: stay dirty so the next change / flush
        // retries instead of trusting a stale server state.
        hasPending.current = true;
        setStatus("error");
        setError(err);
        setIsDirty(true);
        refs.current.onError?.(err, data);
      }
    },
    [mutateAsync],
  );

  const save = useCallback(
    (data: TData) => {
      latestData.current = data;
      if (refs.current.shouldSave && !refs.current.shouldSave(data)) {
        return;
      }
      hasPending.current = true;
      setIsDirty(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        timer.current = null;
        hasPending.current = false;
        void runSave(data);
      }, debounceMs);
    },
    [debounceMs, runSave],
  );

  const cancel = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    hasPending.current = false;
  }, []);

  const flush = useCallback(async () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    if (!hasPending.current && statusRef.current !== "error") return;
    const data = latestData.current;
    if (data == null) return;
    if (refs.current.shouldSave && !refs.current.shouldSave(data)) return;
    hasPending.current = false;
    await runSave(data);
  }, [runSave]);

  // Teardown flush. The debounce timer and in-flight request are destroyed on
  // unload, so without this a change made within the debounce window (or a
  // request still in flight) would be lost. sendBeacon hands the final snapshot
  // to the browser to deliver during teardown.
  useEffect(() => {
    function flushOnHide() {
      const data = latestData.current;
      const dirty = hasPending.current || inFlight.current !== null;
      if (!dirty || data == null) return;

      const build = refs.current.beacon;
      if (!build || typeof navigator === "undefined" || !navigator.sendBeacon) {
        return;
      }
      if (refs.current.shouldSave && !refs.current.shouldSave(data)) return;

      const payload = build(data);
      if (!payload) return;

      // Drop the in-flight request (possibly older state) in favour of the
      // beacon carrying the current snapshot as the final write.
      if (inFlight.current) inFlight.current.abort();
      if (timer.current) clearTimeout(timer.current);
      hasPending.current = false;

      const blob = new Blob([payload.body], {
        type: payload.contentType ?? "application/json",
      });
      navigator.sendBeacon(payload.url, blob);
    }

    function onVisibilityChange() {
      if (document.visibilityState === "hidden") flushOnHide();
    }

    window.addEventListener("pagehide", flushOnHide);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.removeEventListener("pagehide", flushOnHide);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  // Abort any in-flight request and cancel pending timers on unmount.
  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current);
      if (inFlight.current) inFlight.current.abort();
    };
  }, []);

  return { status, error, lastSavedAt, isDirty, save, cancel, flush };
}
